import { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { StreamChatContext } from "../components/StreamChatProvider";
import { UserContext } from "../components/UserContext";
import ConversationList from "../components/Chat/ConversationList";
import ConversationView from "../components/Chat/ConversationView";
import axios from "axios";
import Cookies from "js-cookie";

interface Conversation {
  id: number;
  streamChannelId: string;
  bookingId: number;
  workspaceId: number;
  workspaceTitle: string;
  workspaceImageUrl: string | null;
  otherUserId: number;
  otherUserName: string;
  otherUserImage: string | null;
  lastMessageAt: string | null;
}

export default function MessagesPage() {
  const { user } = useContext(UserContext);
  const { client, isConnected } = useContext(StreamChatContext);
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileViewingConversation, setIsMobileViewingConversation] =
    useState(false);

  useEffect(() => {
    if (!user) return;
    fetchConversations();
  }, [user]);

  useEffect(() => {
    // Handle URL param for direct conversation access
    const conversationId = searchParams.get("conversation");
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find((c) => c.id === parseInt(conversationId));
      if (conv) {
        setSelectedConversation(conv);
        setIsMobileViewingConversation(true);
      }
    }
  }, [searchParams, conversations]);

  const fetchConversations = async () => {
    try {
      const token = Cookies.get("authToken");
      const response = await axios.get(
        `/api/chat/conversations?userId=${user?.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setConversations(response.data.conversations);
        // Auto-select first conversation on desktop
        if (response.data.conversations.length > 0 && window.innerWidth >= 768) {
          setSelectedConversation(response.data.conversations[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setIsMobileViewingConversation(true);
  };

  const handleBackToList = () => {
    setIsMobileViewingConversation(false);
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <p className="text-gray-500">Please log in to view your messages.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--secondary)]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">Messages</h1>

      <div className="flex h-[calc(100vh-200px)] bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Conversation List - 25% on desktop, full on mobile when not viewing conversation */}
        <div
          className={`
          ${isMobileViewingConversation ? "hidden md:block" : "block"}
          w-full md:w-1/4 border-r border-gray-200
        `}
        >
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversation?.id}
            onSelect={handleSelectConversation}
            isLoading={isLoading}
          />
        </div>

        {/* Conversation View - 75% on desktop, full on mobile when viewing */}
        <div
          className={`
          ${isMobileViewingConversation ? "block" : "hidden md:block"}
          w-full md:w-3/4
        `}
        >
          {selectedConversation && client ? (
            <ConversationView
              conversation={selectedConversation}
              client={client}
              onBack={handleBackToList}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <svg
                  className="w-16 h-16 mx-auto mb-4 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
